import React from 'react';
import classnames from 'classnames';
import { TFunction } from 'i18next';
import { withTranslation, WithTranslation } from 'react-i18next';

import { Button } from '@jimdo/ui';

import LoadingAnimation from '../loadingAnimation/loadingAnimation';
import { TldSelect } from '../tldSelect/tldSelect';
import ProductDomainTransferableHint from '../hints/productDomainTransferableHint';
import TransferDomainTransferableHint from '../hints/transferDomainTransferableHint';
import ProductDomainTransformedHint from '../hints/productDomainTransformedHint';
import UsernameMaxLengthHint from '../hints/usernameMaxLengthHint';
import FreeDomainTransformedHint from '../hints/freeDomainTransformedHint';
import FreeTldDetectedHint from '../hints/freeTldDetectedHint';
import { Prices } from '../../redux/actions/options';
import Tile from '../tile/tile';
import { FREE, PRODUCT, TRANSFER, Hints } from '../../redux/reducers/domain-field';
import { FreeDomainHint, ProductDomainHint, TransferDomainHint } from '../../redux/actions/domain';
import { normalizeAndTransformSpecialCharsKeepingTld } from '../../modules/inputTransformator';

import './domainPicker.scss';

const MIN_LENGTH_FOR_USERNAME_LENGTH_HINT = 20;
const MAX_FREE_VALUE_LENGTH = 30;

interface Props {
  active: boolean;
  canContinue: boolean;
  continueWithDomain: () => void;
  error: string;
  tld?: string;
  fieldValue: string;
  rawFieldValue?: string;
  hints?: Hints;
  domainType: string;
  neutralState: boolean;
  placeholder: string;
  badgeText?: string;
  prices?: Prices;
  propagateChange: React.EventHandler<React.ChangeEvent<HTMLInputElement>>;
  propagateTldChange?: React.FormEventHandler<HTMLSelectElement>;
  selectDomainType: (domainType: string) => void;
  domainFieldValueChanged?: (domainType: string, value: string) => {};
  showSuccess: boolean;
  validateDomain: (domainType: string) => void;
  validationPending: boolean;
}

interface State {
  focused: boolean;
}

type DomainPickerProps = Props & WithTranslation;

export class DomainPicker extends React.Component<DomainPickerProps, State> {
  elementToFocus: HTMLElement;

  constructor(props: DomainPickerProps) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.selectDomainType = this.selectDomainType.bind(this);
    this.bindElementToFocus = this.bindElementToFocus.bind(this);
    this.setDomainOnDomainType = this.setDomainOnDomainType.bind(this);
  }

  componentDidUpdate(): void {
    if (this.props.showSuccess && this.elementToFocus) {
      this.elementToFocus.focus();
    }
  }

  // tslint:disable max-func-body-length
  render(): JSX.Element {
    const {
      domainType,
      active,
      neutralState,
      error,
      fieldValue,
      tld,
      placeholder,
      prices,
      showSuccess,
      validationPending,
      propagateChange,
      propagateTldChange,
      canContinue,
      badgeText,
    } = this.props;

    const showError = !!error;

    const wrapperClasses = classnames('domain-picker-component', {
      'domain-picker-component--inactive': !active && !neutralState,
      'domain-picker-component--error': showError,
      'domain-picker-component--success': showSuccess,
      'domain-picker-component--active': active && !showSuccess && !showError,
    });
    const inputWrapperClasses = classnames('domain-picker-component__input-wrapper', {
      'domain-picker-component__input-wrapper--disabled': validationPending,
    });

    const domainPrefix = 'https://';

    const onClick = !active ? this.selectDomainType() : undefined;

    return (
      <form onSubmit={this.handleSubmit}>
        <Tile className={wrapperClasses} onClick={onClick}>
          <label htmlFor={`domainpicker-${domainType}`} className="domain-picker-component__label">
            {this.getHeadline(domainType, this.props.t, prices)}
            {domainType === PRODUCT && badgeText ? (
              <span className="domain-picker-component__label__badge">{badgeText}</span>
            ) : null}
          </label>

          <div className="domain-picker-component__inner">
            <div className={inputWrapperClasses}>
              <span className="domain-picker-component__prefix">{domainPrefix}</span>
              <input
                type="text"
                className="domain-picker-component__input"
                id={`domainpicker-${domainType}`}
                ref={this.bindElementToFocus}
                onChange={propagateChange}
                disabled={validationPending}
                name={domainType}
                value={fieldValue || ''}
                placeholder={placeholder}
                autoFocus={active}
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                maxLength={domainType === FREE ? MAX_FREE_VALUE_LENGTH : null}
              />

              {domainType === FREE ? <span>.jimdofree.com</span> : undefined}
              {domainType === PRODUCT ? (
                <TldSelect
                  handleChange={propagateTldChange}
                  tld={tld}
                  loading={validationPending}
                />
              ) : undefined}
            </div>

            {active ? (
              <Button
                buttonStyle="primary"
                className="domain-picker-component__button"
                disabled={!fieldValue || validationPending || showError}
              >
                {validationPending ? (
                  <LoadingAnimation />
                ) : (
                  this.getButtonText(domainType, canContinue, this.props.t)
                )}
              </Button>
            ) : null}

            {this.renderErrorAndHints()}
          </div>
        </Tile>
      </form>
    );
  }
  // tslint:enable max-func-body-length

  getButtonText(domainType: string, canContinue: boolean, t: TFunction): string {
    if (domainType === FREE) {
      return canContinue
        ? t('signup-flow.domain.free.buttonlabel.continue')
        : t('signup-flow.domain.free.buttonlabel.checkdomain');
    }

    if (domainType === TRANSFER) {
      return canContinue
        ? t('signup-flow.domain.transfer.buttonlabel.continue')
        : t('signup-flow.domain.transfer.buttonlabel.checkdomain');
    }

    return canContinue
      ? t('signup-flow.domain.buttonlabel.continue')
      : t('signup-flow.domain.buttonlabel.checkdomain');
  }

  getHeadline(domainType: string, t: TFunction, prices: Prices): string {
    if (domainType === FREE) {
      return t('signup-flow.domain.label.free');
    }

    if (domainType === PRODUCT) {
      const rawProductHeadline = t('signup-flow.domain.label.product');
      return prices ? rawProductHeadline.replace('{PRICE}', prices.pro) : rawProductHeadline;
    }

    return t('signup-flow.domain.label.transfer');
  }

  selectDomainType(otherDomainType?: string): () => void {
    return (): void => {
      const domainType = otherDomainType ? otherDomainType : this.props.domainType;
      this.props.selectDomainType(domainType);
    };
  }

  handleSubmit(evnt: React.FormEvent<HTMLFormElement>): void {
    evnt.preventDefault();
    this.props.canContinue
      ? this.props.continueWithDomain()
      : this.props.validateDomain(this.props.domainType);
  }

  setDomainOnDomainType(domainValue: string): void {
    this.props.domainFieldValueChanged(PRODUCT, domainValue);
    const selectProductDomainType = this.selectDomainType(PRODUCT);
    selectProductDomainType();
  }

  bindElementToFocus(elem: HTMLElement): void {
    this.elementToFocus = elem;
  }

  renderErrorAndHints(): JSX.Element {
    const { domainType, error, fieldValue, hints, rawFieldValue, tld } = this.props;

    const isProductWithHints = domainType === PRODUCT && hints;
    const isFreeWithHints = domainType === FREE && hints;
    const isTransferWithHints = domainType === TRANSFER && hints;

    const errorText =
      error &&
      this.props
        .t(error)
        .replace('{DOMAIN}', fieldValue + (tld || ''))
        .replace('{TLD}', tld || '');

    // tslint:disable react-no-dangerous-html
    return (
      <div>
        {domainType === FREE && fieldValue.length >= MIN_LENGTH_FOR_USERNAME_LENGTH_HINT ? (
          <UsernameMaxLengthHint
            currentLength={fieldValue.length}
            maxLength={MAX_FREE_VALUE_LENGTH}
            className="domain-picker-component__hint"
          />
        ) : null}
        {isProductWithHints && hints[ProductDomainHint.Transformed] && (
          <ProductDomainTransformedHint className="domain-picker-component__hint" />
        )}
        {isFreeWithHints && hints[FreeDomainHint.Transformed] && (
          <FreeDomainTransformedHint className="domain-picker-component__hint" />
        )}
        {error && (
          <div
            className="domain-picker-component__error-text"
            dangerouslySetInnerHTML={{ __html: errorText }}
          />
        )}
        {isProductWithHints && hints[ProductDomainHint.Transferable] && (
          <ProductDomainTransferableHint
            className="domain-picker-component__hint"
            onClick={this.props.continueWithDomain}
          />
        )}
        {isTransferWithHints && hints[TransferDomainHint.Transferable] && (
          <TransferDomainTransferableHint
            className="domain-picker-component__hint"
            onClick={this.props.continueWithDomain}
          />
        )}
        {isFreeWithHints && hints[FreeDomainHint.TldDetected] && (
          <FreeTldDetectedHint
            className="domain-picker-component__hint"
            domain={normalizeAndTransformSpecialCharsKeepingTld(rawFieldValue)}
            onClick={this.setDomainOnDomainType}
          />
        )}
      </div>
    );
    // tslint:enable react-no-dangerous-html
  }
}

export default withTranslation()(DomainPicker);
